import { useEffect, useState } from "react";
import { Box, Dialog, IconButton, Snackbar, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import { ButtonCustom, CancelButton, ModalInput } from "../shared/Style/Style";
import { DialogHeader } from "../MobileAppModal/MobileAppModal.style";
import { DatePickerField } from "../shared/DataPikerField/DataPikerField";
import {
  useCheckContentByProductIdLazyQuery,
  useCreatePromoCodeCollectionMutation,
  useGetPromoCodeCollectionLazyQuery,
  useUpdatePromoCodeCollectionMutation,
} from "../../generated/graphql";

interface PromoCodesModalProps {
  id: string | null;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

interface FormState {
  name: string;
  description: string;
  recipient: string;
  expiredDate: Date | null;
  codesCount: string;
  contentId: string;
  contentIds: number[];
}

const INITIAL_STATE: FormState = {
  name: "",
  description: "",
  recipient: "",
  expiredDate: null,
  codesCount: "",
  contentId: "",
  contentIds: [],
};

const MAX_CONTENT_IDS = 15;

export const PromoCodesModal = ({ id, onClose, onSuccess }: PromoCodesModalProps) => {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);

  const [createPromoCodeCollection, { loading: createLoading }] = useCreatePromoCodeCollectionMutation();
  const [updatePromoCodeCollection, { loading: updateLoading }] = useUpdatePromoCodeCollectionMutation();
  const [checkContent, { loading: checkLoading }] = useCheckContentByProductIdLazyQuery();
  const [getPromoCodeCollection] = useGetPromoCodeCollectionLazyQuery();

  const getPromoCodeCollectionHandler = async () => {
    try {
      const { data } = await getPromoCodeCollection({
        variables: { id },
        fetchPolicy: "network-only",
      });
      const promoCodeData = data?.getPromoCodeCollection;
      if (!promoCodeData) return;

      setForm((prev) => ({
        ...prev,
        name: promoCodeData.name ?? "",
        description: promoCodeData.description ?? "",
        recipient: promoCodeData.recipient ?? "",
        expiredDate: promoCodeData.expiredDate ? promoCodeData.expiredDate : null,
        contentIds: promoCodeData.productIds ?? [],
      }));
    } catch (e) {
      console.error("getPromoCodeCollectionHandler error", e);
    }
  };

  useEffect(() => {
    if (!id) return;
    getPromoCodeCollectionHandler();
  }, [id]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddContentId = async () => {
    const productId = Number(form.contentId.trim());

    if (form.contentIds.includes(productId) || form.contentIds.length >= MAX_CONTENT_IDS) {
      return;
    }

    const { data } = await checkContent({ variables: { productId } });
    if (!data?.checkContentByProductId) {
      return;
    }

    setForm((prev) => ({ ...prev, contentIds: [...prev.contentIds, productId], contentId: "" }));
  };

  const handleRemoveContentId = (contentId: number) => {
    setForm((prev) => ({ ...prev, contentIds: prev.contentIds.filter((id) => id !== contentId) }));
  };

  const handleClose = () => {
    setForm(INITIAL_STATE);
    onClose();
  };

  const [errorOpen, setErrorOpen] = useState(false);

  const isFormValid = () => {
    const { contentId, codesCount, ...base } = form;
    const baseValid = Object.values(base).every((value) => Array.isArray(value) ? value.length > 0 : !!value);
    return id ? baseValid : baseValid && !!codesCount;
  };

  const handleSubmit = async () => {
    try {
      if (id) {
        await updatePromoCodeCollection({
          variables: {
            id,
            data: {
              name: form.name.trim(),
              description: form.description.trim(),
              recipient: form.recipient.trim(),
              contentIds: form.contentIds,
              expiredDate: form.expiredDate,
            },
          },
        });
      } else {
        await createPromoCodeCollection({
          variables: {
            data: {
              name: form.name.trim(),
              description: form.description.trim(),
              recipient: form.recipient.trim(),
              codesCount: parseInt(form.codesCount, 10),
              contentIds: form.contentIds,
              expiredDate: form.expiredDate,
            },
          },
        });
      }
      await onSuccess();
    } catch {
      setErrorOpen(true);
    }
  };

  const loading = createLoading || updateLoading;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { padding: "32px" } }}
    >
      <DialogHeader mb={2}>
        <Typography variant="h6">{"Тема (заголовок) *"}</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <ModalInput
        fullWidth
        variant="filled"
        label="Назва набору *"
        value={form.name}
        onChange={(e) => setField("name", e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <ModalInput
        fullWidth
        variant="filled"
        label="Опис *"
        multiline
        value={form.description}
        onChange={(e) => setField("description", e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <ModalInput
        fullWidth
        variant="filled"
        label="Отримувач *"
        value={form.recipient}
        onChange={(e) => setField("recipient", e.target.value)}
        style={{ marginBottom: 16 }}
      />

      <DatePickerField
        date={form.expiredDate}
        field="expiredDate"
        label="Дія до *"
        onChange={(_, date) => setField("expiredDate", date)}
        disablePast
        disableToday
      />

      {!id && (
        <ModalInput
          fullWidth
          variant="filled"
          label="Кількість промокодів *"
          type="number"
          value={form.codesCount}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "").slice(0, 3);
            if (!val || (+val >= 1 && +val <= 999)) setField("codesCount", val);
          }}
          style={{ marginBottom: 16 }}
        />
      )}

      <Box
        display="flex"
        alignItems="center"
        style={{ gap: 8, marginBottom: 8 }}
      >
        <ModalInput
          fullWidth
          variant="filled"
          label="ID контенту"
          placeholder="введіть короткий ID контенту"
          value={form.contentId}
          onChange={(e) => setField("contentId", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddContentId()}
        />
        <ButtonCustom
          onClick={handleAddContentId}
          disabled={checkLoading || form.contentIds.length >= MAX_CONTENT_IDS}
          style={{ flexShrink: 0 }}
        >
          Додати
        </ButtonCustom>
      </Box>

      {form.contentIds.map((contentId) => (
        <Box
          key={contentId}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ marginBottom: 4 }}
        >
          <Typography variant="body2">{contentId}</Typography>
          <IconButton
            size="small"
            onClick={() => handleRemoveContentId(contentId)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box
        display="flex"
        justifyContent="space-between"
        style={{ marginTop: 32 }}
      >
        <CancelButton onClick={handleClose} disabled={loading}>
          Скасувати
        </CancelButton>

        <ButtonCustom onClick={handleSubmit} disabled={!isFormValid() || loading}>
          Зберегти
        </ButtonCustom>
      </Box>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorOpen(false)}>
          При збереженні виникла помилка
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
